import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Card } from 'react-bootstrap';
import type { Feature } from 'geojson';
import InteractiveMap, {
  ESRI_SATELLITE_TILE_URL,
  ESRI_SATELLITE_TILE_ATTRIBUTION,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/InteractiveMap/BrazilStatesLayer';
import Button from 'components/DesignSystem/Button';
import { CustomAlert } from 'components/AlertsCollection';
import { PolygonLayerFeatureProperties } from 'components/InteractiveMap/PolygonLayer';
import {
  selectMeteorologyFieldsModelPerformanceMapData,
  selectMeteorologyFieldsModelPerformanceMapLoading,
  selectMeteorologyFieldsModelPerformanceMapError,
  selectMeteorologyFieldsModelPerformancePeriodType,
} from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import { ModelPerformanceMapData } from 'types/Meteorology/Fields/ModelPerformance/mapData';
import useMapDownload from 'hooks/Meteorology/Fields/ModelPerformance/useMapDownload';
import useMapTitle from 'hooks/Meteorology/Fields/ModelPerformance/useMapTitle';
import MapSpinnerControl from './MapSpinnerControl';
import PolygonLegendControl from './PolygonLegendControl';

import './index.scss';

function isModelPerformanceMapData(data: unknown): data is ModelPerformanceMapData {
  return (
    typeof data === 'object'
    && data !== null
    && 'geoJson' in data
    && 'legend' in data
    && 'variable' in data
    && 'unit' in data
  );
}

export default function MapDisplay() {
  const mapData = useSelector(selectMeteorologyFieldsModelPerformanceMapData);
  const mapLoading = useSelector(selectMeteorologyFieldsModelPerformanceMapLoading);
  const mapError = useSelector(selectMeteorologyFieldsModelPerformanceMapError);
  const periodType = useSelector(selectMeteorologyFieldsModelPerformancePeriodType);
  const {
    onDownloadClick,
    setOnDownloadClick,
    filename,
    isDownloadDisabled,
  } = useMapDownload();
  const { title, subtitle, placeholder } = useMapTitle();
  const latestTitleRef = useRef({ title, subtitle, placeholder });
  latestTitleRef.current = { title, subtitle, placeholder };

  const [frozenTitle, setFrozenTitle] = useState('');
  const [frozenSubtitle, setFrozenSubtitle] = useState('');
  const [frozenPlaceholder, setFrozenPlaceholder] = useState('');
  const [selectedFeature, setSelectedFeature] = useState<PolygonLayerFeatureProperties | null>(
    null,
  );

  const hasValidData = isModelPerformanceMapData(mapData)
    && mapData.geoJson?.features?.length > 0;

  useEffect(() => {
    if (hasValidData && mapData) {
      setFrozenTitle(latestTitleRef.current.title);
      setFrozenSubtitle(latestTitleRef.current.subtitle);
      setFrozenPlaceholder(latestTitleRef.current.placeholder);
    } else {
      setFrozenTitle('');
      setFrozenSubtitle('');
      setFrozenPlaceholder('');
    }
  }, [hasValidData, mapData]);

  const clearSelectedFeature = useCallback(() => {
    setSelectedFeature(null);
  }, []);

  const onPolygonClick = useCallback(
    (_feature: Feature, properties: PolygonLayerFeatureProperties) => {
      setSelectedFeature(properties);
    },
    [],
  );

  const alert: CustomAlert | undefined = useMemo(() => (mapError ? {
    title: mapError.title,
    message: mapError.message,
    variant: 'danger',
  } : undefined), [mapError]);

  const isWideScreen = typeof window !== 'undefined' && window.innerWidth >= 1600;

  const mapZoomProps = isWideScreen
    ? {
      center: [-14.235, -51.925] as [number, number],
      zoom: 4.5,
      minZoom: 4.5,
      maxZoom: 4.5,
    }
    : {
      minZoom: 4,
      maxZoom: 4,
    };

  return (
    <Card className="model-performance-map-display shadow-sm overflow-hidden">
      <div className="model-performance-map-display__header d-flex justify-content-between align-items-start gap-2 p-2 border-bottom">
        <div className="model-performance-map-display__header-text flex-grow-1 min-w-0">
          {hasValidData && (frozenTitle || frozenSubtitle) ? (
            <>
              {frozenTitle && (
                <div className="model-performance-map-display__title">{frozenTitle}</div>
              )}
              {frozenSubtitle && (
                <div className="model-performance-map-display__subtitle">{frozenSubtitle}</div>
              )}
            </>
          ) : null}
        </div>
        {hasValidData && (
          <Button
            icon="download"
            type="tertiary"
            onClick={onDownloadClick}
            disabled={isDownloadDisabled()}
            className="flex-shrink-0"
          >
            Download
          </Button>
        )}
      </div>
      <InteractiveMap
        {...mapZoomProps} // eslint-disable-line react/jsx-props-no-spreading
        tileUrl={ESRI_SATELLITE_TILE_URL}
        tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
        dragging={false}
        zoomControl={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        recenterControl={false}
      >
        <InteractiveMap.MapClickHandler onClick={clearSelectedFeature} />
        <MapSpinnerControl isLoading={mapLoading} />
        <InteractiveMap.Alert alert={alert} />
        {hasValidData && (
          <>
            <InteractiveMap.PolygonLayer
              data={mapData.geoJson}
              onPolygonClick={onPolygonClick}
              selectedFeatureName={selectedFeature?.name ?? null}
            />
            <InteractiveMap.ImageDownloader
              setOnDownload={setOnDownloadClick}
              filename={filename}
              caption={frozenPlaceholder}
            />
            <PolygonLegendControl
              legend={mapData.legend}
              variable={mapData.variable}
              unit={mapData.unit}
              selectedFeatureColor={selectedFeature?.color ?? null}
              titleOverride={periodType === 'free' && mapData.variable ? `${mapData.variable} para periodo livre` : null}
            />
          </>
        )}
        <BrazilStatesLayer />
      </InteractiveMap>
      {hasValidData && selectedFeature?.name && (
        <Card.Body className="py-2 small text-muted border-top">
          Selecionado:
          {' '}
          {selectedFeature.name}
        </Card.Body>
      )}
    </Card>
  );
}
